/**
 * Turn-variable registry — the table of `{{name}}` placeholders authors can
 * use inside eval turns, criteria, judgeContext, and groundTruth labels.
 *
 * - **valid** variables resolve to data on the resolved fixture (location or
 *   user). Server reuses `lookupGroundTruth(resolverKey, location, user)`.
 * - **negative** variables resolve to canned values that look real but are
 *   guaranteed not to match any storage org's data — for testing
 *   "no-results" / "not-found" paths without baking specific zip codes into
 *   the case definition.
 *
 * Adding a new variable: append one entry below. The studio's
 * `<TurnVariablePicker>` reads this list directly so the UI updates with no
 * code changes there.
 */
export type TurnVariableKind = "valid" | "negative";
export type TurnVariableGroup = "User" | "Location" | "Negative";

export interface TurnVariableDef {
  /** Token authors write inside the template, without braces. */
  name: string;
  /** Short human label for the picker UI. */
  label: string;
  /** Sentence-long description shown in the picker. */
  description: string;
  /** Group label for the picker UI. */
  group: TurnVariableGroup;
  kind: TurnVariableKind;
  /** Stable key the server-side resolver maps to a fixture value. For `valid`
   *  entries this is fed to `lookupGroundTruth(key, location, user)`. */
  resolverKey: string;
  /** Hardcoded value used when `kind === "negative"`. Required in that case. */
  negativeValue?: string;
}

export const TURN_VARIABLES: readonly TurnVariableDef[] = [
  // ─── User ─────────────────────────────────────────────────────────────────
  {
    name: "userName",
    label: "User name",
    description: "The verified user fixture's display name.",
    group: "User",
    kind: "valid",
    resolverKey: "user.name"
  },
  {
    name: "userPhone",
    label: "User phone",
    description: "The verified user fixture's phone number (E.164).",
    group: "User",
    kind: "valid",
    resolverKey: "user.phone"
  },

  // ─── Location (valid — sourced from the locationFixture) ──────────────────
  {
    name: "ValidLocationName",
    label: "Valid location name",
    description: "Name of the fixture location the case is run against.",
    group: "Location",
    kind: "valid",
    resolverKey: "location.name"
  },
  {
    name: "ValidLocationCity",
    label: "Valid location city",
    description: "City of the fixture location.",
    group: "Location",
    kind: "valid",
    resolverKey: "location.city"
  },
  {
    name: "ValidLocationAddress",
    label: "Valid location address",
    description: "Street address of the fixture location.",
    group: "Location",
    kind: "valid",
    resolverKey: "location.address"
  },
  {
    name: "ValidLocationPhone",
    label: "Valid location phone",
    description: "Phone of the fixture location.",
    group: "Location",
    kind: "valid",
    resolverKey: "location.phone"
  },
  {
    name: "ZipCodeOfValidLocation",
    label: "ZIP of valid location",
    description: "Postal code of the fixture location (matches it 1:1).",
    group: "Location",
    kind: "valid",
    resolverKey: "location.postalCode"
  },
  {
    name: "ValidLocationEvalId",
    label: "Eval-org id of valid location",
    description:
      "The fixture location's id within the eval-org. Used in synthesized widget-action turns (e.g. `synthesizeUserInputFromAction`'s `I picked location \"X\" (id Y).`) so the eval text matches what the production widget would emit.",
    group: "Location",
    kind: "valid",
    resolverKey: "location.evalId"
  },
  {
    name: "ValidUnitName",
    label: "Valid unit name",
    description:
      "A unit name guaranteed to exist in the fixture's seeded inventory. `DataCopier.seedAvailableUnits` always seeds a unit with name=\"A101\" at every fixture location, so this resolves deterministically. Used by stories that reference a specific unit number (us-023 'Is unit X still available?', us-049 'reserve unit X').",
    group: "Location",
    kind: "valid",
    resolverKey: "unit.name"
  },
  // ─── Alt-location (for cases that compare two distinct locations) ─────────
  // Resolves to a *different* location in the same eval-org (different city
  // than the primary). Throws if the eval-org has only one location.
  {
    name: "ValidLocationCityAlt",
    label: "Alt valid location city",
    description: "City of a second, different location in the eval-org. Used for compare-locations cases.",
    group: "Location",
    kind: "valid",
    resolverKey: "location.alt.city"
  },
  {
    name: "ValidLocationNameAlt",
    label: "Alt valid location name",
    description: "Name of a second, different location in the eval-org.",
    group: "Location",
    kind: "valid",
    resolverKey: "location.alt.name"
  },

  // ─── Negative (look real but guaranteed not to match any storage org) ─────
  {
    name: "ZipCodeWithNoValidLocation",
    label: "ZIP with no location",
    description: "A real US ZIP guaranteed not to match any storage org (Ketchikan, AK — far north of any feasible facility).",
    group: "Negative",
    kind: "negative",
    resolverKey: "negative.zip",
    negativeValue: "99950"
  },
  {
    name: "CityWithoutLocation",
    label: "City with no location",
    description: 'A real US city guaranteed not to host any storage org ("Adak, AK" — population <200, no feasible self-storage market).',
    group: "Negative",
    kind: "negative",
    resolverKey: "negative.city",
    negativeValue: "Adak, AK"
  }
];

/** Look up a variable by its token name (the part inside `{{ }}`). */
export function findTurnVariable(name: string): TurnVariableDef | undefined {
  return TURN_VARIABLES.find((v) => v.name === name);
}

/** Regex used by the server-side interpolator + studio highlighters. */
export const TURN_VARIABLE_REGEX = /\{\{([a-zA-Z_]\w*)\}\}/g;
